import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Layout from '../components/layout/Layout';
import { useCart } from '../context/CartContext';

const CartPage = () => {
  const { cartItems, updateQuantity, removeFromCart, getCartTotal, clearCart } = useCart();
  const [promoCode, setPromoCode] = useState('');
  const navigate = useNavigate();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ru-RU').format(price);
  };

  const subtotal = getCartTotal();
  const delivery = subtotal >= 30000 ? 0 : 2500;
  const total = subtotal + delivery;

  if (cartItems.length === 0) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-md mx-auto text-center">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <ShoppingBag className="w-12 h-12 text-gray-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Корзина пуста</h1>
            <p className="text-gray-600 mb-6">Добавьте теплицы из каталога, чтобы оформить заказ</p>
            <Link to="/catalog">
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                Перейти в каталог
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Breadcrumbs */}
      <div className="bg-gray-50 py-4">
        <div className="container mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-emerald-600">Главная</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900">Корзина</span>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Корзина</h1>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Cart Items */}
          <div className="flex-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {cartItems.map((item, index) => (
                <div 
                  key={item.id}
                  className={`p-6 flex flex-col sm:flex-row gap-4 ${index > 0 ? 'border-t border-gray-100' : ''}`}
                >
                  <div className="w-full sm:w-32 h-32 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                    <img
                      src={item.images[0]}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <Link 
                      to={`/product/${item.slug}`}
                      className="font-semibold text-gray-900 hover:text-emerald-600 transition-colors"
                    >
                      {item.name}
                    </Link>
                    <p className="text-sm text-gray-500 mt-1">
                      Размер: {item.size.width}x{item.size.length}x{item.size.height} м
                    </p>
                    {/* Отображение выбранных опций */}
                    {item.options && (
                      <div className="mt-2 text-xs bg-emerald-50 rounded p-2 space-y-1">
                        {item.options.polycarbonate && (
                          <p className="text-emerald-700">• Поликарбонат: {item.options.polycarbonate}</p>
                        )}
                        {item.selectedOptions && item.selectedOptions.map((opt, idx) => (
                          opt.price > 0 && <p key={idx} className="text-blue-600">• {opt.name}: +{opt.price.toLocaleString('ru-RU')} ₽</p>
                        ))}
                      </div>
                    )}
                    <p className="text-sm text-emerald-600 font-medium mt-1">
                      {item.inStock ? 'В наличии' : 'Под заказ'}
                    </p>
                  </div>
                  <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between gap-4">
                    <div className="flex items-center border border-gray-200 rounded-lg">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-2 hover:bg-gray-100 transition-colors"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="px-3 font-medium">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-2 hover:bg-gray-100 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg text-gray-900">
                        {formatPrice(item.price * item.quantity)} ₽
                      </p>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-500 text-sm flex items-center gap-1 hover:text-red-600 mt-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        Удалить
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={clearCart}
              className="mt-4 text-gray-500 hover:text-red-500 text-sm transition-colors"
            >
              Очистить корзину
            </button>
          </div>

          {/* Order Summary */}
          <div className="lg:w-96">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sticky top-24">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Ваш заказ</h2>

              {/* Promo Code */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Промокод</label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Введите код"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                  />
                  <Button variant="outline">Применить</Button>
                </div>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Товары ({cartItems.length})</span>
                  <span className="font-medium">{formatPrice(subtotal)} ₽</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Доставка</span>
                  <span className={`font-medium ${delivery === 0 ? 'text-emerald-600' : ''}`}>
                    {delivery === 0 ? 'Бесплатно' : `${formatPrice(delivery)} ₽`}
                  </span>
                </div>
                {delivery > 0 && (
                  <p className="text-xs text-gray-500">
                    Бесплатная доставка при заказе от 30 000 ₽
                  </p>
                )}
              </div>

              <div className="border-t border-gray-200 mt-4 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold">Итого</span>
                  <span className="text-2xl font-bold text-emerald-700">{formatPrice(total)} ₽</span>
                </div>
              </div>

              <Button 
                className="w-full mt-6 bg-emerald-600 hover:bg-emerald-700 h-12"
                onClick={() => navigate('/checkout')}
              >
                Оформить заказ
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <p className="text-xs text-gray-500 text-center mt-4">
                Нажимая "Оформить заказ", вы соглашаетесь с условиями пользовательского соглашения
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CartPage;
